import React, { useState, useEffect, useRef } from 'react';
import { Box, Button, Alert, Typography } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { tokenService, TOKEN_UPDATED_EVENT } from '../../../services/tokenService';

// H5P requests go through the dev proxy (setupProxy.js) → same origin
// In production, configure your reverse proxy to forward /h5p/* to the H5P service

interface H5PContent {
    contentId?: number | string;
    contentTitle?: string;
}

interface H5PEditorProps {
    content: H5PContent;
    onChange: (content: H5PContent) => void;
}

const TOKEN_PUSH_INTERVAL_MS = 40 * 60 * 1000;

const H5PEditor: React.FC<H5PEditorProps> = ({ content, onChange }) => {
    const [editorOpen, setEditorOpen] = useState(false);
    const editorWindowRef = useRef<Window | null>(null);

    const getToken = () => localStorage.getItem('token') || '';
    const contentIdParam = content.contentId ? String(content.contentId) : 'new';
    const buildEditorUrl = () => `/h5p/${contentIdParam}/edit?token=${encodeURIComponent(getToken())}`;

    // Listen for messages from the editor window
    useEffect(() => {
        const handler = (e: MessageEvent) => {
            if (e.data?.type === 'h5p-saved') {
                onChange({ contentId: e.data.contentId, contentTitle: e.data.title });
                setEditorOpen(false);
            }
            if (e.data?.type === 'h5p-editor-loaded') {
                setEditorOpen(true);
            }
        };
        window.addEventListener('message', handler);
        return () => window.removeEventListener('message', handler);
    }, [onChange]);

    // Check if editor window was closed
    useEffect(() => {
        if (!editorOpen) return;
        const interval = setInterval(() => {
            if (editorWindowRef.current?.closed) {
                setEditorOpen(false);
                editorWindowRef.current = null;
            }
        }, 1000);
        return () => clearInterval(interval);
    }, [editorOpen]);

    useEffect(() => {
        if (!editorOpen) return;
        const interval = setInterval(() => {
            tokenService.refreshAccessToken();
        }, TOKEN_PUSH_INTERVAL_MS);
        return () => clearInterval(interval);
    }, [editorOpen]);

    // Issue #295: Wenn der Access-Token im Parent refresht wird (api.ts-
    // Interceptor), muss der bereits geoeffnete H5P-Editor-Iframe den neuen
    // Token mitbekommen — sonst scheitert der naechste Save mit 401, und
    // Walter verliert seine Folie.
    useEffect(() => {
        const onTokenUpdated = (e: Event) => {
            const detail = (e as CustomEvent<{ token: string }>).detail;
            const w = editorWindowRef.current;
            if (!w || w.closed || !detail?.token) return;
            try {
                w.postMessage({ type: 'h5p-token-update', token: detail.token }, '*');
            } catch {
                // Window-Cross-Origin oder bereits zu — ignorieren
            }
        };
        window.addEventListener(TOKEN_UPDATED_EVENT, onTokenUpdated);
        return () => window.removeEventListener(TOKEN_UPDATED_EVENT, onTokenUpdated);
    }, []);

    // Issue #170: Token in der editorUrl ist beim Open fixiert. Walter hatte
    // wiederholt erlebt, dass nach laengerem Editieren der Editor "invalid"
    // anzeigte — Token war abgelaufen. Pre-check verhindert, dass ein Editor
    // mit totem Token geoeffnet wird.
    const tokenValid = tokenService.hasValidToken();

    const openEditor = async () => {
        if (!tokenValid) {
            window.location.reload(); // löst OAuth-Re-Login aus
            return;
        }
        try {
            await tokenService.refreshAccessToken();
        } catch {}
        const w = window.open(buildEditorUrl(), 'h5p-editor', 'width=1200,height=800,menubar=no,toolbar=no');
        if (w) {
            editorWindowRef.current = w;
            setEditorOpen(true);
        }
    };

    return (
        <Box>
            {content.contentId ? (
                <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mb: 2 }}>
                    H5P-Inhalt vorhanden (ID: {content.contentId})
                    {content.contentTitle && ` — ${content.contentTitle}`}
                </Alert>
            ) : (
                <Alert severity="info" sx={{ mb: 2 }}>
                    Erstellen Sie einen interaktiven H5P-Inhalt für diese Lektion.
                    Der Editor öffnet sich in einem neuen Fenster.
                </Alert>
            )}

            {editorOpen && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    Der H5P-Editor ist in einem separaten Fenster geöffnet.
                    Schließen Sie das Fenster wenn Sie fertig sind.
                </Alert>
            )}

            {!tokenValid && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    Ihre Sitzung ist abgelaufen. Bitte melden Sie sich neu an, bevor Sie den Editor öffnen.
                </Alert>
            )}

            <Button
                variant="contained"
                startIcon={<OpenInNewIcon />}
                onClick={openEditor}
                disabled={editorOpen}
            >
                {content.contentId ? 'H5P-Inhalt bearbeiten' : 'H5P-Inhalt erstellen'}
            </Button>
        </Box>
    );
};

export default H5PEditor;
